import * as React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doReportingLoad,
  doReportingSelectReportOpen,
  doSetIsSidebarVisible,
} from 'store/actions';
import { useSelector } from 'hooks/useSelector';
import {
  selectIsSidebarVisible,
  selectRptLoadStatus,
  selectRptReportPages,
} from 'store/remote/selectors';
import { ReportEnum } from 'types/Reports';
import { ICONS } from 'components/Icon';
import { PageEmpty } from 'components/PageEmpty';
import { SyncIndicator } from 'screens/SyncScreen/SyncIndicator';
import { PageEnum, PageTypeEnum, ReportPage } from 'store/remote/types';
import { Main as ReportCheckSchedulePage } from './Reports/ReportCheckSchedule/Main';
import { Main as ReportCheckCompletedPage } from './Reports/ReportCheckCompleted/Main';
import { Main as ReportCheckStatusPage } from './Reports/ReportCheckStatus/Main';
import { Main as ReportElementsPage } from './Reports/ReportElements/Main';
import { Main as ReportAssetsPage } from './Reports/ReportAssets/Main';
import { ReportingNavigation } from './ReportingNavigation';
import {
  createDrawerNavigator,
  DrawerScreenProps,
} from '@react-navigation/drawer';
import { PageUnknown } from './Pages/PagesUnknown/PageUnknown';
import { theme } from 'lib/theme';
import { useFocusEffect } from '@react-navigation/native';
import { smartText } from 'lib/str';
import { EngageScreen } from 'screens/EngageScreen/EngageScreen';
import { navigationRef } from 'components/Navigation/navigationRef';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';

type DrawerParamList = {
  [key: string]: ReportPage;
};
const Drawer = createDrawerNavigator<DrawerParamList>();
type Props = DrawerScreenProps<DrawerParamList>;

const PageSwitch: React.FC<Props> = ({ route: { params } }) => {
  if (params.type === PageTypeEnum.report) {
    if (params.config.type === ReportEnum.checkschedule) {
      return <ReportCheckSchedulePage reportKey={params.key} />;
    }
    if (params.config.type === ReportEnum.checkscompleted) {
      return <ReportCheckCompletedPage reportKey={params.key} />;
    }
    if (params.config.type === ReportEnum.checkstatus) {
      return <ReportCheckStatusPage reportKey={params.key} />;
    }
    if (params.config.type === ReportEnum.elements) {
      return <ReportElementsPage reportKey={params.key} />;
    }
    if (params.config.type === ReportEnum.assets) {
      return <ReportAssetsPage reportKey={params.key} />;
    }
  } else if (params.type === PageTypeEnum.page) {
    if (params.config.type === PageEnum.engage) {
      return <EngageScreen pageKey={params.key} />;
    }
  }
  return <PageUnknown />;
};

export const ReportingTab: React.FC = () => {
  const dispatch = useDispatch();
  const loadStatus = useSelector(selectRptLoadStatus);
  const screenSize = useScreenSize();
  const isSidebarVisible = useSelector(selectIsSidebarVisible);
  const reportPages = useSelector(selectRptReportPages);

  const currentLoadedStatusRef = React.useRef<'error' | 'loaded'>(null);
  const [drawerStyle, setDrawerStyle] = React.useState<'permanent' | 'front'>(
    'permanent',
  );
  const [drawerWidth, setDrawerWidth] = React.useState<number>(300);
  const currentPage = reportPages.find((p) => p.current);

  if (loadStatus === 'error' || loadStatus === 'loaded') {
    currentLoadedStatusRef.current = loadStatus;
  }
  const handleNavigatorLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      setDrawerStyle(
        event.nativeEvent.layout.width > 600 ? 'permanent' : 'front',
      );
      if (screenSize === ScreenSize.small) {
        setDrawerWidth(
          event.nativeEvent.layout.width - theme.paddingHorizontal * 3,
        );
      }
    },
    [screenSize],
  );

  const handleDrawLayout = React.useCallback((event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width) {
      setDrawerWidth(event.nativeEvent.layout.width);
    }
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      if (loadStatus === undefined) {
        dispatch(doReportingLoad());
      }
    }, [dispatch, loadStatus]),
  );

  React.useEffect(() => {
    /** @ts-expect-error page is available to navigate to */
    navigationRef.current?.navigate(currentPage?.key || 'default');
  }, [currentPage]);

  React.useEffect(() => {
    if (drawerStyle === 'front') {
      dispatch(doSetIsSidebarVisible(false));
    }
  }, [dispatch, drawerStyle]);

  const returnDrawContent = React.useCallback(
    () => (
      <ReportingNavigation
        onLayout={handleDrawLayout}
        width={drawerWidth}
        resizable={drawerStyle !== 'front'}
        forceResizable={drawerStyle === 'permanent'}
      ></ReportingNavigation>
    ),
    [handleDrawLayout, drawerWidth, drawerStyle],
  );

  return (
    <View onLayout={handleNavigatorLayout} style={{ flex: 1 }}>
      {currentLoadedStatusRef.current === 'loaded' ? (
        <Drawer.Navigator
          drawerContent={returnDrawContent}
          defaultStatus="closed"
          screenOptions={{
            headerShown: false,
            drawerType: isSidebarVisible ? drawerStyle : 'front',
            drawerStyle: [{ width: drawerWidth, backgroundColor: '#F3F2F8' }],
            swipeEdgeWidth: 48,
          }}
          initialRouteName={
            reportPages.find((p) => p.current)?.key || undefined
          }
        >
          <Drawer.Screen
            key={'default'}
            name={'default'}
            component={() => (
              <PageEmpty
                icon={ICONS.RBPlectrumLight}
                style={{ backgroundColor: theme.colors.background }}
                backgroundColor={theme.colors.background}
                button={{
                  text: 'Open A Page',
                  onPress: () => dispatch(doReportingSelectReportOpen()),
                }}
              />
            )}
          ></Drawer.Screen>
          {reportPages.map((page) => (
            <Drawer.Screen
              key={page.key}
              name={page.key}
              component={PageSwitch}
              initialParams={page}
            ></Drawer.Screen>
          ))}
        </Drawer.Navigator>
      ) : currentLoadedStatusRef.current === 'error' ? (
        <PageEmpty
          icon={ICONS.WifiSlashLight}
          iconCircled={false}
          text={smartText("We couldn't load the page")}
          subtext={smartText(
            'Please check your internet\nconnection and try again',
          )}
          button={{
            text: 'Try Again',
            icon: ICONS.RotateRightLight,
            onPress: () => dispatch(doReportingLoad()),
          }}
        />
      ) : (
        <View />
      )}
      <SyncIndicator showSyncIcon={false} />
    </View>
  );
};
